/**
 * JPEG'lerden konum (GPS) bilgisini YENİDEN KODLAMADAN siler (KVKK — veri
 * minimizasyonu, F9). Pikseller, yön (Orientation) ve diğer EXIF alanları
 * olduğu gibi kalır; dosya boyu değişmez:
 *
 * - EXIF (APP1 "Exif"): IFD0'daki GPSInfo (0x8825) işaretçisinin gösterdiği
 *   GPS IFD'si boşaltılır — girdi sayısı 0, girdiler ve dışarıdaki değerleri
 *   (enlem/boylam rasyonelleri…) sıfırlanır. İşaretçi boş bir IFD'yi gösterir;
 *   yapı geçerli kalır.
 * - XMP (APP1 "http://ns.adobe.com/…"): `exif:GPS*` değerleri aynı uzunlukta
 *   boşlukla değiştirilir.
 *
 * Yalnız SOS'tan (görüntü verisi) önceki başlık bölümüne bakar; bu yüzden
 * çağıran dosyanın yalnız başını (`JPEG_HEAD_BYTES`) okuyup verebilir.
 * Bozuk/beklenmedik yapıda hiçbir şeye dokunmaz, asla throw etmez.
 *
 * Kapsam dışı: HEIC/PNG/WebP içindeki konum (arayüz HEIC için uyarır).
 */

/** Başlık bölümü için okunacak bayt — EXIF + XMP pratikte bunun çok altında. */
export const JPEG_HEAD_BYTES = 1 << 20;

const TYPE_SIZES: Record<number, number> = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 6: 1, 7: 1, 8: 2, 9: 4, 10: 8, 11: 4, 12: 8 };
const GPS_IFD_TAG = 0x8825;
const EXIF_SIG = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00]; // "Exif\0\0"
const XMP_SIGS = ["http://ns.adobe.com/xap/1.0/\0", "http://ns.adobe.com/xmp/extension/\0"];

export function isJpeg(buf: Uint8Array): boolean {
  return buf.length > 3 && buf[0] === 0xff && buf[1] === 0xd8 && buf[2] === 0xff;
}

/**
 * `buf` içindeki GPS bilgisini YERİNDE siler. Bir şey silindiyse `true`.
 * `buf` JPEG değilse ya da konum yoksa hiçbir bayt değişmez.
 */
export function stripJpegGps(buf: Uint8Array): boolean {
  if (!isJpeg(buf)) return false;
  let removed = false;
  let i = 2;
  while (i + 4 <= buf.length) {
    if (buf[i] !== 0xff) return removed; // senkron kaybı → dokunma
    const marker = buf[i + 1];
    if (marker === 0xff) {
      i += 1; // dolgu baytı
      continue;
    }
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      i += 2; // uzunluksuz işaretler
      continue;
    }
    if (marker === 0xda || marker === 0xd9) return removed; // SOS / EOI → görüntü verisi, dur
    const len = (buf[i + 2] << 8) | buf[i + 3];
    if (len < 2) return removed;
    const start = i + 4;
    const end = i + 2 + len;
    if (end > buf.length) return removed; // segment başlık penceresinin dışına taşıyor
    if (marker === 0xe1) {
      if (startsWith(buf, start, EXIF_SIG)) removed = stripExifGps(buf, start + 6, end) || removed;
      else if (XMP_SIGS.some((s) => startsWithText(buf, start, s))) removed = blankXmpGps(buf, start, end) || removed;
    }
    i = end;
  }
  return removed;
}

function startsWith(buf: Uint8Array, at: number, sig: number[]): boolean {
  if (at + sig.length > buf.length) return false;
  return sig.every((b, k) => buf[at + k] === b);
}

function startsWithText(buf: Uint8Array, at: number, text: string): boolean {
  if (at + text.length > buf.length) return false;
  for (let k = 0; k < text.length; k++) if (buf[at + k] !== text.charCodeAt(k)) return false;
  return true;
}

/** TIFF yapısı `t`'de başlar, `end`'de biter (segment sınırı). */
function stripExifGps(buf: Uint8Array, t: number, end: number): boolean {
  if (t + 8 > end) return false;
  const le = buf[t] === 0x49 && buf[t + 1] === 0x49; // "II"
  const be = buf[t] === 0x4d && buf[t + 1] === 0x4d; // "MM"
  if (!le && !be) return false;
  const u16 = (o: number) => (le ? buf[o] | (buf[o + 1] << 8) : (buf[o] << 8) | buf[o + 1]);
  const u32 = (o: number) =>
    (le
      ? buf[o] | (buf[o + 1] << 8) | (buf[o + 2] << 16) | (buf[o + 3] << 24)
      : (buf[o] << 24) | (buf[o + 1] << 16) | (buf[o + 2] << 8) | buf[o + 3]) >>> 0;
  if (u16(t + 2) !== 42) return false;

  const ifd0 = t + u32(t + 4);
  if (ifd0 + 2 > end) return false;
  const n0 = u16(ifd0);
  if (ifd0 + 2 + n0 * 12 > end) return false;
  let gps = -1;
  for (let k = 0; k < n0; k++) {
    const e = ifd0 + 2 + k * 12;
    if (u16(e) === GPS_IFD_TAG) gps = t + u32(e + 8);
  }
  if (gps < 0 || gps + 2 > end) return false;
  const n = u16(gps);
  if (n === 0) return false;
  const tableEnd = gps + 2 + n * 12 + 4; // sayaç + girdiler + sonraki-IFD işaretçisi
  if (tableEnd > end) return false;

  // Önce dışarıdaki değerler (4 bayttan uzunlar), sonra tablonun kendisi.
  for (let k = 0; k < n; k++) {
    const e = gps + 2 + k * 12;
    const size = (TYPE_SIZES[u16(e + 2)] ?? 0) * u32(e + 4);
    if (size > 4) {
      const at = t + u32(e + 8);
      if (at >= t && at + size <= end) buf.fill(0, at, at + size);
    }
  }
  buf.fill(0, gps, tableEnd);
  return true;
}

const XMP_ATTR_RE = /exif:GPS[A-Za-z]*\s*=\s*(["'])/g;
const XMP_ELEM_RE = /<(exif:GPS[A-Za-z]*)(\s[^>]*)?>/g;

/** XMP metni (latin1 olarak — bayt ↔ karakter birebir) içinde GPS değerlerini boşlukla ez. */
function blankXmpGps(buf: Uint8Array, start: number, end: number): boolean {
  let text = "";
  for (let k = start; k < end; k++) text += String.fromCharCode(buf[k]);
  let removed = false;
  // Yalnız boşluk olmayan bir değer ezildiyse "silindi" say (ikinci geçiş no-op).
  const blank = (from: number, to: number) => {
    if (to > from && /\S/.test(text.slice(from, to))) {
      buf.fill(0x20, start + from, start + to);
      removed = true;
    }
  };

  for (const m of text.matchAll(XMP_ATTR_RE)) {
    const valueStart = m.index! + m[0].length;
    const close = text.indexOf(m[1], valueStart);
    if (close > 0) blank(valueStart, close);
  }
  for (const m of text.matchAll(XMP_ELEM_RE)) {
    const contentStart = m.index! + m[0].length;
    const close = text.indexOf(`</${m[1]}>`, contentStart);
    if (close > 0) blank(contentStart, close);
  }
  return removed;
}

/**
 * Blob/File sarmalayıcı (tarayıcı + worker): yalnız başlık penceresini okur;
 * konum varsa aynı türde yeni bir Blob döner (görüntü verisi kopyalanmaz,
 * dilim olarak eklenir). Hata ya da JPEG değilse girdinin kendisi döner.
 */
export async function stripGpsFromBlob(blob: Blob): Promise<{ blob: Blob; removed: boolean }> {
  try {
    const head = new Uint8Array(await blob.slice(0, JPEG_HEAD_BYTES).arrayBuffer());
    if (!stripJpegGps(head)) return { blob, removed: false };
    return { blob: new Blob([head, blob.slice(head.length)], { type: blob.type }), removed: true };
  } catch {
    return { blob, removed: false };
  }
}
